import { clone } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService, UserProfileSettingsService } from '../../system-services';
import { ODataQueryBuilder } from './odata-query-builder.util';
import {
    ODataFilter,
    ODataPageInfo,
    ODataSearch,
    ODataSearchState,
    ODataSort,
    ODataSearchResult,
    defaultResultsPerPage
} from './odata.models';

/**
 * Base class for building out OData service implementations.  Query functions return the service so that calls can be chained.
 * Queries are only executed once 'getResults()' is called.
 */
export abstract class ODataServiceBase<T> {
    protected _searchState$: BehaviorSubject<ODataSearchState<T>> =
        new BehaviorSubject(null);

    protected searchState: ODataSearchState<T>;

    protected oDataSearch: ODataSearch = null;

    constructor(
        protected api: ApiService,
        protected userProfile: UserProfileSettingsService
    ) {}

    /**
     * A copy of the current ODataSearch query
     */
    public get search(): ODataSearch {
        return clone(this.oDataSearch);
    }

    /**
     * A copy of the current ODataSearch state
     */
    public get state(): ODataSearchState<T> {
        return clone(this.searchState);
    }

    public get searchState$(): Observable<ODataSearchState<T>> {
        return this._searchState$;
    }

    private get hasResults(): boolean {
        return this.searchState?.hasResults;
    }

    private get getMaxPages() {
        return Math.ceil(
            this.searchState?.searchResults?.totalRecords /
                this.oDataSearch.pageInfo.resultsPerPage
        );
    }

    /**
     * Retrieve the results using the current ODataSearch query
     *
     * @param search the search object to be used, if one is provided this object will
     * be used instead of the one currently built in the service
     * @param companyId the id of the company
     * @returns the OData search result set
     */
    public async getResults<U = any>(
        search: ODataSearch = this.oDataSearch,
        companyId = this.userProfile.currentCompany
    ): Promise<ODataSearchResult<T>> {
        const oDataSearch = this.initQueryObject();
        let result = null;
        try {
            this.searchState.isLoading = true;
            this._searchState$.next(this.searchState);
            result = await this.internalGetResults(
                search ?? oDataSearch,
                companyId
            );
        } catch (err) {
            this.searchState.hasResults = false;
            throw err;
        } finally {
            // do stuff for search state observable/results observable here
            this.searchState.isLoading = false;
            this.searchState.searchResults = result;
            this.searchState.hasResults = !!result;
            this._searchState$.next(this.searchState);
        }

        return result;
    }

    /**
     * Adds a filter to the odata query
     *
     * @param oDataFilter the filter to be added
     * @returns The service
     */
    public addFilter(oDataFilter: ODataFilter): this {
        const oDataSearch = this.initQueryObject();
        oDataSearch.filters.push(oDataFilter);
        return this;
    }

    /**
     * Removes a filter from the current odata query
     *
     * @param oDataFilter the filter to be removed
     * @returns The service
     */
    public removeFilter(oDataFilter: ODataFilter): this {
        const oDataSearch = this.initQueryObject();
        oDataSearch.filters = this.oDataSearch.filters.filter(
            (x) => x.key !== oDataFilter.key
        );
        return this;
    }

    /**
     * Sets the filter of the current odata query
     *
     * @param oDataFilter the filter to be set
     * @returns The service
     */
    public setFilter(oDataFilter: ODataFilter): this {
        const oDataSearch = this.initQueryObject();
        oDataSearch.filters = [oDataFilter];
        return this;
    }

    /**
     * Set the sort of the current odata query
     *
     * @param oDataSort The sort to be used
     * @returns The Service
     */
    public setSort(oDataSort: ODataSort): this {
        const oDataSearch = this.initQueryObject();
        oDataSearch.sort = oDataSort;
        return this;
    }

    /**
     * Clears the current sort of the ODataQuery.  Will set to the default sort value.
     *
     * @returns The service
     */
    public clearSort(): this {
        const oDataSearch = this.initQueryObject();
        oDataSearch.sort = this.getDefaultSort();
        return this;
    }

    /**
     * Sets the page info for the current query
     *
     * @param pageInfo The page info to be set
     * @returns The Service
     */
    public setPageInfo(pageInfo: ODataPageInfo): this {
        const oDataSearch = this.initQueryObject();
        oDataSearch.pageInfo = pageInfo;
        return this;
    }

    /**
     * Update the query to go to the next page of results.  Will not change if there are no results.
     * Cannot go beyond the total results value
     *
     * @returns The service.
     */
    public nextPage(): this {
        if (!this.hasResults) {
            return this;
        }

        const totalRecords = this.searchState.searchResults.totalRecords;
        if (
            this.oDataSearch.pageInfo.currentPage *
                this.oDataSearch.pageInfo.resultsPerPage <
            totalRecords
        ) {
            this.oDataSearch.pageInfo.currentPage += 1;
        }
        return this;
    }

    /**
     * Go to the previous page, will not update if there are no results.
     * Cannot go below page 1.
     *
     * @returns The service
     */
    public previousPage(): this {
        if (!this.hasResults) {
            return this;
        }

        if (this.oDataSearch.pageInfo.currentPage === 1) {
            return this;
        }

        this.oDataSearch.pageInfo.currentPage -= 1;
        return this;
    }

    /**
     * Sets the pagination info to the provided page number
     *
     * @param pageNumber the page number to be set
     * @returns The service
     */
    public goToPage(pageNumber: number): this {
        if (!this.hasResults) {
            return this;
        }

        if (
            pageNumber < 1 ||
            pageNumber > this.getMaxPages ||
            pageNumber === this.search.pageInfo.currentPage
        ) {
            return this;
        }

        this.oDataSearch.pageInfo.currentPage = pageNumber;
        return this;
    }

    /**
     * Clears the current odata query
     */
    public clearQuery() {
        this.oDataSearch = null;
    }

    /**
     * Clears the search results
     */
    public clearResults() {
        this.searchState.searchResults = null;
        this._searchState$.next(this.searchState);
    }

    /**
     * API call for retrieving the odata result set
     *
     * @param search The search object
     * @param companyId the company id
     * @returns the odata search result
     */
    protected async internalGetResults(
        search: ODataSearch,
        companyId: string
    ): Promise<ODataSearchResult<T>> {
        const [response] = await this.api.get<ODataSearchResult<T>>(
            `${this.buildODataUrl(companyId)}${this.buildODataQuery(search)}`
        );
        return response;
    }

    /**
     * Builds out the odata query string based on the search object
     *
     * @param search The search object
     * @returns the query string
     */
    protected buildODataQuery(search: ODataSearch): string {
        return ODataQueryBuilder.buildODataQuery(search);
    }

    /**
     * Builds out the odata search initial object.  This is called in all query functions at the start in order to initialize
     * the query.
     */
    private initQueryObject(): ODataSearch {
        if (this.oDataSearch) {
            return this.oDataSearch;
        }

        this.oDataSearch = {
            pageInfo: {
                resultsPerPage:
                    this.userProfile.preferredPageSize || defaultResultsPerPage,
                currentPage: 1
            },
            filters: [],
            sort: this.getDefaultSort()
        };
        this.searchState = {
            searchResults: null,
            oDataSearch: this.oDataSearch
        };
        return this.oDataSearch;
    }

    /**
     * Function used to build out the ODataUrl.  This must be implemented by the inheriting service.
     *
     * @param companyId the id of the requesting user company
     */
    public abstract buildODataUrl(companyId: string): string;

    /**
     * Provides a default sort when making odata queries
     */
    public abstract getDefaultSort(): ODataSort;
}
